import JustValidate from 'just-validate'
import { select, selectAll } from 'helpers/dom'

const selectors = {
  ERROR_MESSAGE_SELECTOR: '.js-input-error-message'
}

const settings = {
  errorFieldCssClass: 'is-invalid',
  errorLabelCssClass: 'input-error js-input-error-message',
  errorLabelStyle: {
    color: '#DC2626'
  },
  focusInvalidField: true,
  lockForm: false
}

const allRules = {
  email: {
    rule: 'email',
    errorMessage: 'Email is invalid!'
  },
  password: {
    validator: (value) => value.length >= 5,
    errorMessage: 'Password must be at least 5 characters'
  },
  number: {
    rule: 'number'
  },
  tel: {
    validator: (value) => {
      const regex = /\(?([0-9]{3})\)?([ .-]?)([0-9]{3})\2([0-9]{4})/

      return !value || regex.test(value)
    },
    errorMessage: 'Phone number is invalid'
  },
  postcode: {
    validator: (value) => {
      const regex = /\^(0[289][0-9]{2})$|^([1-9][0-9]{3})$/

      return regex.test(value) && value.length <= 4
    },
    errorMessage: 'Postcode is invalid'
  }
}

/**
 * Handles validation failure.
 * @param {Object} fields - The fields that failed validation.
 */
const handleValidationFail = (fields) => {
  Object.values(fields).forEach(({ elem: fieldEl }) => {
    const errorMessageEl = select(selectors.ERROR_MESSAGE_SELECTOR, fieldEl.parentElement)

    if (!fieldEl.hasAttribute('id')) return

    if (!errorMessageEl) {
      fieldEl.removeAttribute('aria-describedby')
    } else {
      const errorId = `error-${fieldEl.id}`

      errorMessageEl.id = errorId
      fieldEl.setAttribute('aria-describedby', errorId)
    }
  })
}

/**
 * Adds a confirmation rule to an input field.
 * @param {Array} rules - The validation rules array.
 * @param {HTMLElement} input - The input element.
 * @returns {Array} The updated validation rules array.
 */
const addRuleConfirm = (rules, input) => {
  const confirmField = input.getAttribute('data-confirm')
  const confirmMessage = input.getAttribute('data-confirm-message')

  rules.push({
    validator: (value, fields) => value === fields[confirmField]?.elem?.value,
    errorMessage: confirmMessage
  })

  return rules
}

/**
 * Generates validation rules for a given input.
 * @param {HTMLElement} input - The input element.
 * @returns {Object} The validation rules.
 * @throws {Error} If the input is not a valid HTMLElement.
 */
const getValidateRule = (input) => {
  if (!(input instanceof HTMLElement)) {
    throw new Error('Invalid input for getValidateRule')
  }

  if (!input.id) {
    throw new Error('Form input must have id')
  }

  const inputName = input.getAttribute('aria-label') || 'Field'
  const rules = {
    id: `#${input.id}`,
    rules: []
  }

  if (input.required) {
    rules.rules.push({
      rule: 'required',
      errorMessage: `${inputName} is required`
    })
  }

  if (input.type in allRules) {
    rules.rules.push(allRules[input.type])
  }

  if (input.max && input.type !== 'date') {
    rules.rules.push({
      rule: 'maxLength',
      value: parseInt(input.max, 10)
    })
  }

  if (input.min && input.type !== 'date') {
    rules.rules.push({
      rule: 'minLength',
      value: parseInt(input.min, 10)
    })
  }

  if (input.name === 'postcode') {
    rules.rules.push(allRules.postcode)
  }

  if (input.hasAttribute('data-confirm') && input.hasAttribute('data-confirm-message')) {
    rules.rules = addRuleConfirm(rules.rules, input)
  }

  return rules
}

/**
 * Initializes validation for a form.
 * @param {HTMLElement} formEl - The form element.
 * @param {Function} [callback=false] - The callback function to be executed after successful validation.
 * @param {Boolean} [autoSubmit=true] - Whether to automatically submit the form after successful validation.
 * @throws {Error} If formEl is not a valid HTMLElement.
 * @returns {JustValidate|null} The JustValidate instance or null if no inputs are found.
 */
const initValidate = (formEl, callback = false, autoSubmit = true) => {
  if (!(formEl instanceof HTMLElement) || !formEl.id) {
    throw new Error('formEl must be a valid DOM element with an id property')
  }

  const formId = `form#${formEl.id}`
  const inputs = selectAll('input:not([type=hidden]), select, textarea', formEl)

  if (!inputs.length) return null

  const validate = new JustValidate(formId, settings)
  const newRules = inputs.map(getValidateRule)

  newRules.forEach(({ id, rules }) => rules.length > 0 && validate.addField(id, rules))

  if (autoSubmit && !callback) {
    validate.onSuccess((event) => formEl.submit(event))
    validate.onFail(handleValidationFail)
  }

  return validate
}


export default initValidate
